// src/components/DashboardHeader.js - With toast notifications
import React from 'react';
import { Menu, X, Eye, Upload, Copy, QrCode } from 'lucide-react';
import IconBackground from './IconBackground';
import MorphingText from './MorphingText';
import { useToast } from '../context/ToastContext'; // Import useToast hook
import styles from './Dashboard.module.css';

const DashboardHeader = ({ 
  menuOpen, 
  setMenuOpen, 
  setActiveSection, 
  handlePublish, 
  handleCopyProfile,
  openQrCodeModal,
  openFullScreenPreview,
  isProUser 
}) => {
  const { showSuccess, showError, showInfo } = useToast(); // Get toast functions

  // Enhanced publish handler with toast notification
  const handlePublishWithToast = async () => {
    try {
      // Show "Publishing..." toast while operation is in progress
      showInfo("Publishing your profile...");
      
      // Call the original publish handler and await its result
      const result = await handlePublish();
      
      // Show success toast when complete
      showSuccess("Profile published successfully! Your changes are now live.");
      
      return result;
    } catch (error) {
      // Show error toast if something goes wrong
      showError(`Publishing failed: ${error.message || 'Unknown error'}`);
      throw error; // Re-throw to allow caller to handle
    }
  };

  // Enhanced copy handler with toast notification
  const handleCopyWithToast = () => {
    try {
      const result = handleCopyProfile();
      showSuccess("Profile link copied to clipboard!");
      return result;
    } catch (error) {
      showError("Could not copy profile link.");
      console.error(error);
    }
  };

  // Preview handler with toast
  const handlePreviewWithToast = () => {
    showInfo("Opening preview...");
    openFullScreenPreview();
  };

  // QR code handler with toast
  const handleQrCodeWithToast = () => {
    showInfo("Opening QR code...");
    openQrCodeModal();
  };

  return (
    <div>
      <IconBackground />
      <MorphingText context="dashboard" />
      {/* Menu Toggle Button - will be hidden on mobile via CSS */}
      <div className={styles.menuToggleContainer}>
        <button 
          className={`${styles.menuToggle} ${menuOpen ? styles.menuOpen : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X size={32} /> : <Menu size={32} />}
        </button>
      </div>
      {/* Banner with Action Buttons */}
      <div className={styles.banner}>
        <div className={`${styles.bannerActions} ${menuOpen ? styles.shifted : ''}`}>
          <button 
            className={styles.previewButton}
            onClick={handlePreviewWithToast}
          >
            <Eye size={22} />
            <span className={styles.buttonText}>Preview</span>
          </button>
          <button 
            className={styles.publishButton}
            onClick={handlePublishWithToast}
          >
            <Upload size={22} />
            <span className={styles.buttonText}>Publish</span>
          </button>
          <button 
            className={styles.copyButton}
            onClick={handleCopyWithToast}
          >
            <Copy size={22} />
            <span className={styles.buttonText}>Copy</span>
          </button>
          <button 
            className={styles.qrCodeButton}
            onClick={handleQrCodeWithToast}
          >
            <QrCode size={22} />
            <span className={styles.buttonText}>QR</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardHeader;